import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize, take } from 'rxjs';
import { GlobalSearchDatatableService } from './global-search-datatable.service';
import { DASHBOARD_TABLE_CONFIG, DASHBOARD_TABLE_STATE } from 'app/home-dashboard/home-dashboard-table.config';
import { DataTableColumnFilters, DataTableColumnFiltersApi, DataTableColumnModel, DataTableConfigSettings, DataTableStateModel } from '../dynamic-table/dynamic-table.model';
import { DataTableNamesEnum } from 'app/shared/enums/datatable-names.enum';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { DynamicTableComponent } from '../dynamic-table/dynamic-table.component';
import { CommonModule } from '@angular/common';
import { PROBLEM_RECORDS_DASHBOARD_TABLE_STATE, PROBLEM_RECORDS_TABLE_CONFIG } from 'app/problem-records/problem-records-table.config';
import { TitleHeaderComponent } from '../title-header/title-header.component';
import { GlobalSearchComponent } from '../global-search/global-search.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CreateCasePopupComponent } from '../create-case-popup/create-case-popup.component';
import { GlobalSearchEnum } from 'app/shared/enums/global-search.enum';
import { GlobalSearchModel } from 'app/shared/models/global-search.model';
import { HelperService } from 'app/shared/services/helper.service';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

@Component({
  selector: 'app-global-search-datatable',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    DynamicTableComponent,
    TitleHeaderComponent,
    GlobalSearchComponent,
    TranslateModule,
    CreateCasePopupComponent
  ],
  templateUrl: './global-search-datatable.component.html',
  styleUrl: './global-search-datatable.component.scss'
})
export class GlobalSearchDatatableComponent implements OnInit {
  gridUrl!: string;
  globalSearch!: string;
  totalCount: number = 0;
  columns: any = [];
  rows: any = [];
  dashboardDataTableState!: DataTableStateModel;
  dashboardDataTableStateFromStorage!: DataTableStateModel;
  datatableDisplayAllNames: DataTableColumnFilters[] = [];
  isDataTableReady: boolean = false;
  filters: DataTableColumnFiltersApi[] = [];
  title: string = '';
  searchData = {
    cases: [],
    problemRecords: [],
    knowledgeLibrary: []
  } as GlobalSearchModel;
  isOnInit: boolean = true;

  defaultColumns = [];
  selectedFilterName = '';

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private globalSearchDatatableService: GlobalSearchDatatableService,
    private configurationService: ConfigurationService,
    private helperService: HelperService,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {
    this.activeRoute.params
      .pipe(take(1))
      .subscribe((params: any) => {
        if (params['gridUrl']) {
          this.gridUrl = params['gridUrl'];
        }
      });

    this.activeRoute.queryParams
      .pipe(take(1))
      .subscribe((params: any) => {
        this.globalSearch = params['search'];
      });
  }

  ngOnInit(): void {
    switch (this.gridUrl) {
      case GlobalSearchEnum.GRID_URL_CASES:
        this.dashboardDataTableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DataTableNamesEnum.SEARCH_CASE_DATATABLE);
        this.dashboardDataTableState = this.dashboardDataTableStateFromStorage ? this.dashboardDataTableStateFromStorage : { ...DASHBOARD_TABLE_STATE };
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_CASE_DATATABLE;
        this.columns = this.helperService.deepCopy(DASHBOARD_TABLE_CONFIG);
        this.title = `title.global_search.${GlobalSearchEnum.ITEM_KEY_CASES}`;
        this.defaultColumns = this.helperService.deepCopy(DASHBOARD_TABLE_CONFIG);

        break;
      case GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS:
        this.dashboardDataTableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DataTableNamesEnum.SEARCH_PROBLEM_RECORD_DATATABLE);
        this.dashboardDataTableState = this.dashboardDataTableStateFromStorage ? this.dashboardDataTableStateFromStorage : { ...PROBLEM_RECORDS_DASHBOARD_TABLE_STATE };
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_PROBLEM_RECORD_DATATABLE;
        this.columns = this.helperService.deepCopy(PROBLEM_RECORDS_TABLE_CONFIG);
        this.title = `title.global_search.${GlobalSearchEnum.ITEM_KEY_PROBLEM_RECORDS}`;
        this.defaultColumns = this.helperService.deepCopy(PROBLEM_RECORDS_TABLE_CONFIG);

        break;
      case GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY:
        this.dashboardDataTableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DataTableNamesEnum.SEARCH_KNOWLEDGE_LIBRARY_DATATABLE);
        this.dashboardDataTableState = this.dashboardDataTableStateFromStorage ? this.dashboardDataTableStateFromStorage : { ...DASHBOARD_TABLE_STATE };
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_KNOWLEDGE_LIBRARY_DATATABLE;
        this.columns = this.helperService.deepCopy(DASHBOARD_TABLE_CONFIG);
        this.title = `title.global_search.${GlobalSearchEnum.ITEM_KEY_KNOWLEDGE_LIBRARY}`;
        this.defaultColumns = this.helperService.deepCopy(DASHBOARD_TABLE_CONFIG);

        break;
    }

    this.getDataTableConfiguration();

  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.dashboardDataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          switch (this.gridUrl) {
            case GlobalSearchEnum.GRID_URL_CASES:
              this.setCaseData();
              break;
            case GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS:
              this.setProblemRecordData();
              break;
            case GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY:
              this.setCaseData();
              break;
          }
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.dashboardDataTableStateFromStorage) {
          this.columns = this.dashboardDataTableState.datatableColumns;
          this.filters = this.dashboardDataTableState.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.dashboardDataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.dashboardDataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }
        if (res && res.name) {
          this.selectedFilterName = res.name;
        }
      });
  }

  setCaseData() {
    this.setDataTableColumnFilters();
    this.datatableDisplayAllNames = [
      ...this.configurationService.getStatusItems(),
      ...this.configurationService.getNextActionStatusItemsForFilter(),
      ...this.configurationService.getCaseStageItems(),
      ...this.configurationService.getBooleanDataTableColumnFilters(),
      ...this.configurationService.getSeverityItems(),
    ];

    this.isDataTableReady = true;
    this.getData();
  }

  setProblemRecordData() {
    this.setDataTableColumnFilters();
    this.datatableDisplayAllNames = [
      ...this.configurationService.getProblemRecordPriorityItems(),
      ...this.configurationService.getProblemRecordStatusItems(),
      ...this.configurationService.getProblemRecordStageItems(),
      ...this.configurationService.getNextActionStatusItemsForFilter(),
      ...this.configurationService.getBooleanDataTableColumnFilters(),
    ];

    this.isDataTableReady = true;
    this.getData();
  }

  setDataTableColumnFilters() {
    this.columns.forEach((column: any) => {
      switch (column.prop) {
        case 'caseStatus':
          column.dataTableColumnFilters = this.configurationService.getStatusItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
        case 'caseStage':
          column.dataTableColumnFilters = this.configurationService.getCaseStageItems();
          break;
        case 'severity':
          column.dataTableColumnFilters = this.configurationService.getSeverityItems();
          break;
        case 'problemPriority':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordPriorityItems();
          break;
        case 'problemStatus':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordStatusItems();
          break;
        case 'problemStage':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordStageItems();
          break;
      }
    });
  }

  stripHtmlTags(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  getData() {
    this.rows = [];
    this.globalSearchDatatableService.getElkDataTableSearch(this.gridUrl, this.globalSearch, this.dashboardDataTableState)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.rows = (res.items ?? []).map((item: any) => ({
          ...item,
          msDynamicsReferenceNumber: item.msDynamicsReferenceNumber ? this.stripHtmlTags(item.msDynamicsReferenceNumber) : ''
        }));
        this.totalCount = res.totalCount;

        if (this.isOnInit) {

          switch (this.gridUrl) {
            case GlobalSearchEnum.GRID_URL_CASES:
              this.searchData.cases = this.globalSearchDatatableService
                .mapSearchData(GlobalSearchEnum.GRID_URL_CASES, this.helperService.deepCopy(res.items)).slice(0, 3);

              break;
            case GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS:
              this.searchData.problemRecords = this.globalSearchDatatableService
                .mapSearchData(GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS, this.helperService.deepCopy(res.items)).slice(0, 3);

              break;
            case GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY:
              this.searchData.knowledgeLibrary = this.globalSearchDatatableService
                .mapSearchData(GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY, this.helperService.deepCopy(res.items)).slice(0, 3);

              break;
          }

          this.isOnInit = false;
        }
      });
  }

  getDataTableState(data: any) {
    this.dashboardDataTableState = data;
    this.getData();
  }

  getLinkedValue(data: any) {
    let url = '';
    let property = '';

    switch (data.column) {
      case 'caseIdentifier':
        url = 'case-form/';
        property = data.row.id;
        break;
      case 'problemRecord':
        url = 'problem-record/';
        property = data.row.problemRecordId;
        break;
      case 'problemRecordIdentifier':
        url = 'problem-record/';
        property = data.row.id;
        break;
    }

    this.router.navigate([url + property]);

  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);
    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    switch (this.gridUrl) {
      case GlobalSearchEnum.GRID_URL_CASES:
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_CASE_DATATABLE;
        break;
      case GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS:
         this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_PROBLEM_RECORD_DATATABLE;
        break;
      case GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY:
         this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_KNOWLEDGE_LIBRARY_DATATABLE;
        break;
    }

    const params = {
      gridType: this.dashboardDataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || ''
    }

    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe();
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    switch (this.gridUrl) {
      case GlobalSearchEnum.GRID_URL_CASES:
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_CASE_DATATABLE;
        break;
      case GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS:
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_PROBLEM_RECORD_DATATABLE;
        break;
      case GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY:
        this.dashboardDataTableState.gridName = DataTableNamesEnum.SEARCH_KNOWLEDGE_LIBRARY_DATATABLE;
        break;
    }

    const params = {
      gridType: this.dashboardDataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }

  onShowAllResults(data: string) {
    this.globalSearch = data;
    this.getData();
  }

  openCreateCasePopup() {
    this.helperService.createCasePopoupVisible = true;
    this.helperService.isSidebar = false;
  }

}
